/**
 * The storefront's URL ↔ locale rules.
 *
 * <p>Exactly one URL per page per locale, which is the whole reason this phase
 * exists. Concretely:
 *
 * <ul>
 *   <li>the site's default locale is <strong>unprefixed</strong> — `/p/x`;</li>
 *   <li>every other locale is prefixed with its language subtag — `/tr/p/x`,
 *       a subdirectory rather than a separate TLD, so it inherits the domain's
 *       authority;</li>
 *   <li>every other spelling of a locale — the default locale written out
 *       (`/en-GB/p/x`, `/en-gb/p/x`, `/en/p/x`), or a non-default locale in
 *       any form but its canonical prefix (`/tr-TR/p/x`, `/TR/p/x`) —
 *       <strong>301s</strong> to the one canonical path.</li>
 * </ul>
 *
 * <p><strong>Nothing here hard-codes a language.</strong> The locale set comes
 * from the site descriptor (`GET /public/storefront/site`), which is per
 * tenant: a tenant with `en-GB` default and `tr-TR` second gets exactly the
 * URLs above, and a tenant with a different pair gets theirs. `en` and `tr`
 * appear in this file's prose and its tests only.
 */

/** One locale, and the path prefix it owns. `prefix === null` = unprefixed. */
export interface LocaleRoute {
  /** BCP 47 tag exactly as the backend spelled it, e.g. `en-GB`. */
  tag: string;
  /** Canonical URL segment, or null for the default (unprefixed) locale. */
  prefix: string | null;
  isDefault: boolean;
}

export interface SiteLocaleInput {
  defaultLocale: string;
  locales: Array<{ tag: string; isDefault?: boolean }>;
}

const language = (tag: string): string => {
  const dash = tag.indexOf('-');
  return (dash > 0 ? tag.slice(0, dash) : tag).toLowerCase();
};

/**
 * Turn the site descriptor's locale list into the routing table.
 *
 * <p>The prefix is the language subtag — short, conventional, and what the
 * plan's `/tr/p/x` decision assumes. When two non-default locales share a
 * language (`de-DE` and `de-AT`), a bare `de` would be ambiguous, so those fall
 * back to the full lower-cased tag. The default locale never needs a prefix and
 * therefore never collides.
 */
export function buildLocaleRoutes(site: SiteLocaleInput): LocaleRoute[] {
  const defaultTag =
    site.locales.find((l) => l.isDefault)?.tag ?? site.defaultLocale ?? site.locales[0]?.tag;
  if (!defaultTag) return [];

  const others = site.locales.map((l) => l.tag).filter((tag) => tag !== defaultTag);
  const langCounts = new Map<string, number>();
  for (const tag of others) langCounts.set(language(tag), (langCounts.get(language(tag)) ?? 0) + 1);

  const routes: LocaleRoute[] = [{ tag: defaultTag, prefix: null, isDefault: true }];
  for (const tag of others) {
    const lang = language(tag);
    // A language shared with the DEFAULT locale is not ambiguous — the default
    // is unprefixed, so `de` is still free for the other one.
    const ambiguous = (langCounts.get(lang) ?? 0) > 1;
    routes.push({ tag, prefix: ambiguous ? tag.toLowerCase() : lang, isDefault: false });
  }
  return routes;
}

/**
 * Every segment that names this locale, lower-cased.
 *
 * <p>The canonical prefix is INCLUDED, which is not redundant: the caller
 * checks for an exact, case-sensitive canonical match first and only reaches
 * here if that missed, so `tr` in this set is what turns `/TR/p/x` into a 301
 * to `/tr/p/x` rather than treating `TR` as an ordinary first path segment.
 */
function aliasesFor(route: LocaleRoute, routes: LocaleRoute[]): string[] {
  const lang = language(route.tag);
  const aliases = new Set<string>([route.tag.toLowerCase(), lang]);
  if (route.prefix) aliases.add(route.prefix);

  if (!route.isDefault) {
    // A bare language belongs to whichever locale canonically owns it. With
    // `en-GB` default and `en-US` second, `/en/...` means the default one.
    const defaultOwnsLanguage = routes.some((other) => other.isDefault && language(other.tag) === lang);
    // And when two NON-default locales share a language (`de-DE`, `de-AT`),
    // a bare `de` names neither — it is dropped rather than resolved to
    // whichever happens to be listed first.
    const siblings = routes.filter((other) => !other.isDefault && language(other.tag) === lang);
    if (defaultOwnsLanguage || siblings.length > 1) aliases.delete(lang);
  }
  return [...aliases];
}

export type LocaleResolution =
  /** The URL is canonical. Render `tag`; `rest` is the path with no prefix. */
  | { kind: 'render'; tag: string; prefix: string | null; rest: string }
  /** The URL names a locale in a non-canonical form. 301 to `to`. */
  | { kind: 'redirect'; to: string; tag: string };

/**
 * Resolve a request path against the routing table.
 *
 * @param pathname a locale-unaware, basePath-free path, always leading-slashed
 */
export function resolveLocalePath(pathname: string, routes: LocaleRoute[]): LocaleResolution {
  const fallback = routes.find((r) => r.isDefault) ?? routes[0];
  const path = pathname.startsWith('/') ? pathname : `/${pathname}`;
  if (!fallback) return { kind: 'render', tag: '', prefix: null, rest: path };

  const segments = path.split('/').filter(Boolean);
  const first = segments[0];
  if (!first) return { kind: 'render', tag: fallback.tag, prefix: null, rest: '/' };

  const rest = `/${segments.slice(1).join('/')}`;

  // Canonical prefix of a non-default locale: render, prefix stays in the URL.
  const canonical = routes.find((r) => r.prefix !== null && r.prefix === first);
  if (canonical) {
    return { kind: 'render', tag: canonical.tag, prefix: canonical.prefix, rest };
  }

  // Any other spelling of any locale: 301 to that locale's canonical path.
  const lowered = first.toLowerCase();
  for (const route of routes) {
    if (aliasesFor(route, routes).includes(lowered)) {
      const target = route.prefix ? `/${route.prefix}${rest === '/' ? '' : rest}` : rest;
      return { kind: 'redirect', to: target === '' ? '/' : target, tag: route.tag };
    }
  }

  // Not a locale segment at all — the default locale, unprefixed.
  return { kind: 'render', tag: fallback.tag, prefix: null, rest: path };
}

/**
 * The public path for `path` in `tag` — the only way a canonical, hreflang,
 * sitemap or JSON-LD URL is ever built. Nothing composes one from a slug.
 */
export function localePath(path: string, tag: string, routes: LocaleRoute[]): string {
  const route = routes.find((r) => r.tag === tag);
  const rest = path.startsWith('/') ? path : `/${path}`;
  if (!route?.prefix) return rest;
  return rest === '/' ? `/${route.prefix}` : `/${route.prefix}${rest}`;
}

/**
 * The tenant locale a caller's tag means, or null when the tenant serves no
 * locale in that language.
 *
 * <p>For the admin's preview, which asks for a locale by whatever the operator
 * picked in the editor — a bare language (`en`) or a full tag (`en-GB`) —
 * rather than by this site's URL prefixes. An exact tag wins; otherwise the
 * first locale sharing the language does, so `en` on a tenant serving `en-GB`
 * previews in English instead of falling back to the default language.
 */
export function matchLocaleTag(
  wanted: string | null | undefined,
  routes: readonly LocaleRoute[],
): string | null {
  const tag = wanted?.trim();
  if (!tag) return null;
  const lowered = tag.toLowerCase();
  const exact = routes.find((route) => route.tag.toLowerCase() === lowered);
  if (exact) return exact.tag;
  return routes.find((route) => language(route.tag) === language(tag))?.tag ?? null;
}
